import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Eye, Play, RefreshCw, RotateCw, Trash2, XCircle } from 'lucide-react';
import { quizzesApi } from '../../lib/api';
import type { QuizQuestion, VideoQuizStats } from '../../types/quizzes';
import { formatTime, sortByAt } from '../../lib/videoQuiz';
import { Button, ErrorMessage, Spinner } from '../ui';

const RANGES = [7, 30, 90] as const;

function timeAgo(ts: number): string {
  // Accept seconds or milliseconds.
  const ms = ts < 1e12 ? ts * 1000 : ts;
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Tile({ label, value, icon: Icon, tone }: { label: string; value: number; icon: typeof Eye; tone?: 'danger' | 'success' }) {
  return (
    <div className="border border-line rounded-lg p-3 bg-surface">
      <div className="flex items-center justify-between text-xs text-ink-3 font-medium">
        {label}
        <Icon className={`w-4 h-4 ${tone === 'danger' ? 'text-danger' : tone === 'success' ? 'text-success' : 'text-ink-3'}`} />
      </div>
      <div className="font-display font-bold text-2xl text-ink mt-1">{value.toLocaleString()}</div>
    </div>
  );
}

export default function VideoInsightsPanel({ quizId, questions }: { quizId: string; questions: QuizQuestion[] }) {
  const [days, setDays] = useState<number>(30);
  const [stats, setStats] = useState<VideoQuizStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await quizzesApi.getVideoStats(quizId, days);
    if (res.success && res.data) setStats(res.data);
    else setError(res.message || 'Could not load insights');
    setLoading(false);
  }, [quizId, days]);

  useEffect(() => { load(); }, [load]);

  const act = async (sessionId: string, action: 'retry' | 'delete') => {
    if (action === 'delete' && !confirm('Delete this failed submission? Its answers will be gone for good.')) return;
    setBusy(sessionId);
    const res = action === 'retry'
      ? await quizzesApi.retryVideoSession(quizId, sessionId)
      : await quizzesApi.deleteVideoSession(quizId, sessionId);
    setBusy(null);
    if (!res.success) { setError(res.message || `Could not ${action}`); return; }
    if (action === 'delete') setStats((s) => (s ? { ...s, failed: s.failed.filter((f) => f.id !== sessionId) } : s));
    else load();
  };

  const daily = stats?.daily ?? [];
  const totals = daily.reduce(
    (t, d) => ({
      views: t.views + (d.views || 0), starts: t.starts + (d.starts || 0), completions: t.completions + (d.completions || 0),
      synced: t.synced + (d.synced || 0), failed: t.failed + (d.failed || 0),
    }),
    { views: 0, starts: 0, completions: 0, synced: 0, failed: 0 },
  );

  const reachedById = new Map((stats?.checkpoints ?? []).map((c) => [c.questionId, c.reached]));
  const ordered = sortByAt(questions);
  const base = Math.max(totals.starts, ...ordered.map((q) => reachedById.get(q.id) ?? 0), 1);

  return (
    <div className="space-y-6">
      <div className="p-3 bg-accent-soft border border-line rounded-lg text-sm text-accent-ink">
        Video quiz answers live in ClickFunnels. Once a viewer's answers sync, MyLeadSyncer deletes them and keeps only the anonymous counts below.
        Submissions that couldn't sync are kept here (for up to 30 days) so you can retry them.
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r} type="button" onClick={() => setDays(r)}
              className={`px-3 py-1.5 rounded-[10px] text-xs font-medium border ${days === r ? 'bg-accent-soft border-accent text-accent-ink' : 'border-line text-ink-3 hover:border-ink-3'}`}>
              Last {r} days
            </button>
          ))}
        </div>
        <Button variant="ghost" icon={RefreshCw} onClick={load} disabled={loading}>Refresh</Button>
      </div>

      {error && (
        <div className="p-3 bg-danger-soft border border-line rounded-lg text-danger text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> <ErrorMessage message={error} />
        </div>
      )}

      {loading && !stats ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : stats && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <Tile label="Views" value={totals.views} icon={Eye} />
            <Tile label="Starts" value={totals.starts} icon={Play} />
            <Tile label="Completions" value={totals.completions} icon={CheckCircle2} />
            <Tile label="Synced" value={totals.synced} icon={CheckCircle2} tone="success" />
            <Tile label="Failed" value={totals.failed} icon={XCircle} tone="danger" />
          </div>
          {stats.pending > 0 && <p className="text-xs text-ink-3 -mt-3">{stats.pending} viewer{stats.pending === 1 ? ' is' : 's are'} still in progress.</p>}

          <section>
            <h3 className="text-sm font-semibold text-ink mb-1">Drop-off by question</h3>
            <p className="text-xs text-ink-3 mb-3">How many viewers reached each question, out of {totals.starts.toLocaleString()} who started.</p>
            {ordered.length === 0 ? (
              <p className="text-sm text-ink-3">No questions yet.</p>
            ) : (
              <div className="space-y-2">
                {ordered.map((q, i) => {
                  const n = reachedById.get(q.id) ?? 0;
                  const pct = Math.round((n / base) * 100);
                  return (
                    <div key={q.id} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,16rem)_1fr_auto] items-center gap-x-3 gap-y-1">
                      <div className="text-xs text-ink-2 truncate" title={q.title}>
                        {i + 1}. {q.title} {q.at !== undefined && <span className="text-ink-3 font-mono">({formatTime(q.at)})</span>}
                      </div>
                      <div className="h-3 rounded-full bg-surface-2 border border-line overflow-hidden col-span-2 sm:col-span-1 order-last sm:order-none">
                        <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-xs text-ink-3 font-mono text-right w-24">{n.toLocaleString()} · {pct}%</div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section>
            <h3 className="text-sm font-semibold text-ink mb-1">Failed syncs</h3>
            <p className="text-xs text-ink-3 mb-3">These submissions couldn't be sent to ClickFunnels. They retry automatically (up to 8 times) and are removed after 30 days.</p>
            {stats.failed.length === 0 ? (
              <p className="text-sm text-ink-3">Nothing failed.</p>
            ) : (
              <div className="overflow-x-auto border border-line rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-surface-2 text-xs text-ink-3">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">Email</th>
                      <th className="text-left font-medium px-3 py-2">Attempts</th>
                      <th className="text-left font-medium px-3 py-2">Last error</th>
                      <th className="text-left font-medium px-3 py-2">Updated</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {stats.failed.map((f) => (
                      <tr key={f.id} className="border-t border-line">
                        <td className="px-3 py-2 text-ink">{f.email || <span className="text-ink-3">—</span>}</td>
                        <td className="px-3 py-2 text-ink-2">{f.attempts}</td>
                        <td className="px-3 py-2 text-ink-3 text-xs max-w-[320px] truncate" title={f.lastError ?? ''}>{f.lastError || '—'}</td>
                        <td className="px-3 py-2 text-ink-3 text-xs whitespace-nowrap">{f.updatedAt ? timeAgo(f.updatedAt) : '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex justify-end gap-1">
                            <button type="button" disabled={busy === f.id} onClick={() => act(f.id, 'retry')} className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-line text-ink-2 hover:border-accent hover:text-accent-ink disabled:opacity-50">
                              <RotateCw className="w-3 h-3" /> Retry
                            </button>
                            <button type="button" disabled={busy === f.id} onClick={() => act(f.id, 'delete')} className="p-1 text-ink-3 hover:text-danger disabled:opacity-50" aria-label="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
